import { Container, LoadingOverlay, useDisclosure } from '@hover/blueprint';
import React, { useEffect } from 'react';
import { generatePath, useHistory } from 'react-router';

import { LeadSheet } from 'src/modules/homeownerApp/LeadSheet/LeadSheet';
import { EmbedFooter } from 'src/modules/homeownerApp/components/Embed/components/EmbedFooter';
import { EmbedHeader } from 'src/modules/homeownerApp/components/Embed/components/EmbedHeader';
import { useFeaturedDisplayImages } from 'src/modules/homeownerApp/components/Embed/hooks/useFeaturedDisplayImages';
import { useViewSampleImageEmbed } from 'src/modules/homeownerApp/components/Embed/hooks/useViewSampleImageEmbed';
import {
  randomizeArray,
  useActiveOriginalImage,
} from 'src/modules/homeownerApp/hooks';
import { PartnerImageProvider } from 'src/modules/homeownerApp/hooks/usePartner';
import { HomeownerAppProvider } from 'src/modules/homeownerApp/providers';
import { MY_UPLOAD_STYLE } from 'src/modules/homeownerApp/static_values';
import { isSample } from 'src/modules/homeownerApp/types';
import type { AiDisplayImageFragment } from 'src/shared/graphql/generated';
import { useGlobalStorageContext } from 'src/shared/hooks/useGlobalStorage';
import { useSetOnetrustCookiesManually } from 'src/shared/hooks/useSetOnetrustCookiesManually';
import { useEmbedContext } from 'src/shared/providers/Embed';
import { Route } from 'src/shared/route';

import { EmbedLandingPage } from './EmbedLandingPage';

export const EmbedContainer = () => {
  const {
    isLoading: viewSampleImageEmbedLoading,
    canViewSampleImageEmbed,
    originalImageId,
  } = useViewSampleImageEmbed();

  useSetOnetrustCookiesManually();

  const history = useHistory();

  const { image, loading: originalImageLoading } = useActiveOriginalImage({
    originalImageId,
  });

  const { featuredDisplayImages } = useFeaturedDisplayImages(
    Number(originalImageId),
  );
  const { hasLeadForm } = useEmbedContext();
  const {
    isOpen: isLeadSheetOpen,
    onClose: onLeadSheetClose,
    onToggle: onLeadSheetToggle,
  } = useDisclosure();
  const { affOrgPublicId } = useGlobalStorageContext();

  const redirectQueryParam = affOrgPublicId
    ? `?aff_org_id=${affOrgPublicId}`
    : '';

  const isSampleImage = isSample(image);

  const aiDisplayImageId = isSampleImage
    ? undefined
    : image?.aiDisplayImages?.filter(
        (aiDisplayImage: AiDisplayImageFragment) =>
          aiDisplayImage.style?.toLowerCase() === MY_UPLOAD_STYLE.toLowerCase(),
      )[0].id;

  useEffect(() => {
    if (aiDisplayImageId) {
      if (!canViewSampleImageEmbed) {
        history.push({
          pathname: generatePath(Route.HOMEOWNER_APP_CUSTOMIZE_INSPIRE.path, {
            aiDisplayImageId,
          }),
        });
      }
    }
  }, [aiDisplayImageId, history, canViewSampleImageEmbed]);

  const designImage = randomizeArray(
    featuredDisplayImages?.filter(
      featuredDisplayImage => featuredDisplayImage.style !== MY_UPLOAD_STYLE,
    ) || [],
  )[0];

  return (
    <HomeownerAppProvider>
      <LoadingOverlay
        color="neutral.900"
        isLoading={viewSampleImageEmbedLoading || originalImageLoading}
      />
      <PartnerImageProvider
        aiDisplayImageId={designImage?.id}
        skip={!designImage}
      >
        <Container
          background="neutral.900"
          display="flex"
          flexDirection="column"
          height="100vh"
          justifyContent="space-between"
          width="auto"
        >
          {!originalImageLoading && image && featuredDisplayImages && (
            <>
              <EmbedHeader />
              {canViewSampleImageEmbed && (
                <EmbedLandingPage
                  featuredDisplayImages={featuredDisplayImages}
                  onLeadSheetToggle={onLeadSheetToggle}
                  originalImage={image.aiDisplayImages?.find(
                    img => img.createdFrom === 'original',
                  )}
                  staticDesignedImage={designImage}
                />
              )}
              <EmbedFooter
                background="neutral.900"
                isVertical
                onLeadSheetToggle={onLeadSheetToggle}
              />
              {hasLeadForm && (
                <LeadSheet
                  isOpen={isLeadSheetOpen}
                  onClose={onLeadSheetClose}
                  redirectTo={`/designer/projects/new${redirectQueryParam}`}
                />
              )}
            </>
          )}
        </Container>
      </PartnerImageProvider>
    </HomeownerAppProvider>
  );
};
